/**
 * Upload zone of one pending piece (`depot` — fiches 22975 / 22976), shown
 * by `PiecesCard` on the usages that bind a `PieceUploadConfig`.
 *
 * States (mockups 22975-1/-2, 22976-1/-2):
 * - **empty** — the base `FileUpload` (compact) as a click / drag-and-drop
 *   zone: « **Cliquez** ou glissez un fichier ici ». A file over
 *   `config.maxFileSize` is rejected before it is read (message under the
 *   zone).
 * - **chosen** — a `Panel` with the file icon, its name, « Fichier joint »
 *   (green), the primary « Valider » `Button` and the × ghost `Button` that
 *   resets the zone.
 * - **submitting** — « Valider » loading and × disabled (a second submit
 *   would archive the document twice).
 * - **success** — success toast, zone reset; the pieces list, the tab badge,
 *   the header bell, the Accueil banner and « Pièces à fournir » are
 *   refetched (`usePieceUpload`).
 * - **error** — an error `Alert` below; the file is kept so the user can
 *   retry. A call 2 failure (document archived, piece not updated) has its
 *   own text.
 *
 * Every text comes from the config; the zone's accessible name and the
 * « Valider » label arrive already resolved against the card's overrides.
 */

import { useRef, useState } from 'react';
import { FileCheck, X } from 'lucide-react';

import { Button } from '@components/base/actions';
import { Icon, Text } from '@components/base/display';
import { Alert } from '@components/base/feedback';
import { FileUpload } from '@components/base/inputs';
import { HStack, Panel, VStack } from '@components/base/layout';

import { useOptionalToast } from '../../hooks/useOptionalToast';
import { usePieceUpload } from '../../hooks/usePieceUpload';
import type { PieceUploadTarget } from './pieceItems';
import {
  fileTooLargeMessage,
  uploadFailureOf,
  type PieceUploadConfig,
  type PieceUploadFailure,
} from './pieceUploadConfig';

/** The controlled (always empty) value of the drop zone. */
const NO_FILES: File[] = [];

export interface PieceUploadZoneProps {
  /** Upload ids of the piece. */
  target: PieceUploadTarget;
  /** Piece label — completes the zone's accessible name. */
  pieceLabel: string | null;
  config: PieceUploadConfig;
  /** Accessible name of the zone (the `depot` item label, overridable). */
  zoneLabel: string;
  /** « Valider » label (`setActionLabel submit`). */
  submitLabel: string;
}

export function PieceUploadZone({
  target,
  pieceLabel,
  config,
  zoneLabel,
  submitLabel,
}: Readonly<PieceUploadZoneProps>) {
  const { text } = config;
  const [file, setFile] = useState<File | null>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const [failure, setFailure] = useState<PieceUploadFailure | null>(null);
  const upload = usePieceUpload();
  const toast = useOptionalToast();
  // Synchronous guard against a double submit (before `isPending` re-renders).
  const inFlight = useRef(false);
  const submitting = upload.isPending;

  const accessibleName = pieceLabel ? `${zoneLabel} : ${pieceLabel}` : zoneLabel;

  const choose = (files: File[]) => {
    const chosen = files[0];
    if (chosen === undefined) return;
    if (chosen.size > config.maxFileSize) {
      setSizeError(fileTooLargeMessage(config));
      return;
    }
    setSizeError(null);
    setFailure(null);
    setFile(chosen);
  };

  const reset = () => {
    if (inFlight.current) return;
    setFile(null);
    setFailure(null);
  };

  const submit = () => {
    if (file === null || inFlight.current) return;
    inFlight.current = true;
    setFailure(null);
    upload.mutate(
      { target, file },
      {
        onSuccess: () => {
          setFile(null);
          toast({ variant: 'success', title: text.successTitle, message: text.success });
        },
        onError: (error) => {
          setFailure(uploadFailureOf(error, text));
        },
        onSettled: () => {
          inFlight.current = false;
        },
      },
    );
  };

  if (file === null) {
    return (
      <div data-testid="piece-upload-zone" data-upload-state="empty">
        <FileUpload
          compact
          value={NO_FILES}
          onChange={choose}
          prompt={
            <>
              <Text as="span" size="sm" weight="semibold">
                {text.click}
              </Text>{' '}
              <Text as="span" size="sm" weight="normal" color="muted">
                {text.drop}
              </Text>
            </>
          }
          hint={null}
          dropzoneLabel={accessibleName}
          error={sizeError !== null}
          errorMessage={sizeError ?? undefined}
        />
      </div>
    );
  }

  return (
    <VStack
      gap={2}
      data-testid="piece-upload-zone"
      data-upload-state={submitting ? 'submitting' : 'chosen'}
    >
      <Panel variant="outlined" padding="compact" data-testid="piece-upload-selected">
        <HStack gap={3} align="center" justify="between" wrap>
          <HStack gap={2} align="center" className="min-w-0 flex-1">
            <Icon icon={FileCheck} size="md" color="success" />
            <Text as="span" size="sm" weight="medium" truncate className="min-w-0" title={file.name}>
              {file.name}
            </Text>
            <Text as="span" size="sm" color="success" className="shrink-0">
              {text.attached}
            </Text>
          </HStack>
          <HStack gap={2} align="center" className="shrink-0">
            <Button variant="primary" size="sm" loading={submitting} onClick={submit}>
              {submitLabel}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label={text.reset}
              disabled={submitting}
              onClick={reset}
              leftIcon={<Icon icon={X} size="sm" />}
            />
          </HStack>
        </HStack>
      </Panel>
      {failure !== null && (
        <Alert variant="error" title={failure.title}>
          {failure.message}
        </Alert>
      )}
    </VStack>
  );
}
